import { createSelector, Selector, ParametricSelector } from '@reduxjs/toolkit';
import { ProductTourState } from '../';

const _getViewedProductTours = (state: ProductTourState) => state.viewedProductTours;
const _getViewedProductToursLoaded = (state: ProductTourState) => state.viewedProductToursLoaded;

interface TourIdProps<T> {
  tourId: T;
}

export interface ProductTourSelectors<R, T> {
  getViewedProductTours: Selector<R, Partial<Record<string, boolean>>>;
  getViewedProductToursLoaded: Selector<R, boolean>;
  makeGetTourIdViewed: () => ParametricSelector<R, TourIdProps<T>, boolean>;
  makeShouldShowTourId: () => ParametricSelector<R, TourIdProps<T>, boolean>;
}

export function makeProductTourSelectors<R, T extends string>(
  selector: (root: R) => ProductTourState,
): ProductTourSelectors<R, T> {
  const getTourIdFromProps = (_: R, props: TourIdProps<T>) => props.tourId;

  const getViewedProductTours: Selector<R, Partial<Record<string, boolean>>> = createSelector([selector], _getViewedProductTours);
  const getViewedProductToursLoaded: Selector<R, boolean> = createSelector([selector], _getViewedProductToursLoaded);

  const makeGetTourIdViewed: () => ParametricSelector<R, TourIdProps<T>, boolean> = () => createSelector(
    [getViewedProductTours, getTourIdFromProps],
    (viewedTourIds, tourId) => viewedTourIds[tourId] || false,
  );

  const makeShouldShowTourId: () => ParametricSelector<R, TourIdProps<T>, boolean> = () => createSelector(
    [makeGetTourIdViewed()],
    (viewed) => !viewed,
  );

  return {
    getViewedProductTours,
    getViewedProductToursLoaded,
    makeGetTourIdViewed,
    makeShouldShowTourId,
  };
}
